import type { ReactNode } from "react"
import { createContext, createElement, useContext, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { MergeProposalInput } from "@productlane/lib/src/prosemirror-diff"
import { queries } from "@productlane/zero/queries"

import type {
	AggregatedSuggestions,
	RawProposal,
	RawProposalGroup,
	SuggestionProposal,
	SuggestionRow,
} from "./aggregate-proposals"
import { useIsAgentSidebarShown } from "../agent/agent-sidebar-section"
import { useAgentOwnedProposalGroupIds } from "../agent/use-agent-recommendations"
import { docsPageRows } from "./agent-owned-rows"
import { aggregateProposals } from "./aggregate-proposals"

// Re-export the pure aggregator's types so consumers can import everything
// from one place. Pure aggregation lives in `aggregate-proposals.ts` (no
// React) so it can be unit-tested without mounting a Zero query subscription.
export type {
	AggregatedSuggestions,
	RawProposal,
	RawProposalGroup,
	SuggestionKind,
	SuggestionProposal,
	SuggestionRow,
} from "./aggregate-proposals"
export { aggregateProposals } from "./aggregate-proposals"

// Build the input shape `mergeProposalsForArticle` expects from a normalized
// proposal. DELETE proposals always carry empty `proposedSteps` (set at
// creation time), so they pass through the merge loop as no-ops with
// `applied: true, conflicts: []`. CREATE proposals' steps materialize from
// an empty base into the new article body — only used by the CREATE preview.
export function toMergeInput(p: SuggestionProposal): MergeProposalInput {
	return {
		id: p.id,
		baseHtml: p.baseArticleContent ?? "",
		stepsJson: p.proposedSteps,
	}
}

interface SuggestionsBundle extends AggregatedSuggestions {
	// `rows` the Docs page reviews (see `docsPageRows`): what the Docs sidebar,
	// its J/K order and the review flow's next-suggestion jump walk through.
	visibleRows: SuggestionRow[]
	// Empty until children mount.
	loaded: boolean
}

const SuggestionsContext = createContext<SuggestionsBundle | null>(null)

// Wraps the help-center screens that need access to OPEN proposals. Mount
// once near the route root so `useArticleSuggestions()` calls deeper in the
// tree share a single `useQuery` subscription and a single `useMemo`-
// computed bundle. Without the provider, every consumer would re-run the
// (large) aggregation.
export function HelpCenterSuggestionsProvider({
	children,
}: {
	children: ReactNode
}) {
	const bundle = useArticleSuggestionsRaw()
	return createElement(SuggestionsContext.Provider, { value: bundle }, children)
}

// Aggregates OPEN proposals (standalone + group children) into one row per
// target article. Must be called inside a `HelpCenterSuggestionsProvider`.
export function useArticleSuggestions(): SuggestionsBundle {
	const fromContext = useContext(SuggestionsContext)
	if (!fromContext) {
		throw new Error(
			"useArticleSuggestions must be called inside <HelpCenterSuggestionsProvider>",
		)
	}
	return fromContext
}

// Number of distinct help-center entries that have at least one OPEN proposal
// — the same row count `useArticleSuggestions` produces, exposed without the
// provider so it can drive the Docs nav-item badge in the global sidebar.
export function useOpenProposalRowCount(): number {
	const [standaloneProposals] = useQuery(queries.openProposalsForWorkspace())
	const [groups] = useQuery(queries.openProposalGroupsForCount())
	const agentOwnedGroupIds = useAgentOwnedProposalGroupIds()
	const agentShown = useIsAgentSidebarShown()
	return useMemo(
		() =>
			docsPageRows(
				aggregateProposals({
					standalone: standaloneProposals as readonly RawProposal[],
					groups: groups as readonly RawProposalGroup[],
				}).rows,
				agentOwnedGroupIds,
				agentShown,
			).length,
		[standaloneProposals, groups, agentOwnedGroupIds, agentShown],
	)
}

function useArticleSuggestionsRaw(): SuggestionsBundle {
	const [standaloneProposals, standaloneResult] = useQuery(
		queries.openProposalsForWorkspace(),
	)
	const [groups, groupsResult] = useQuery(
		queries.openProposalGroupsForWorkspace(),
	)
	const agentOwnedGroupIds = useAgentOwnedProposalGroupIds()
	const agentShown = useIsAgentSidebarShown()

	return useMemo(() => {
		const aggregated = aggregateProposals({
			standalone: standaloneProposals as readonly RawProposal[],
			groups: groups as readonly RawProposalGroup[],
		})
		return {
			...aggregated,
			visibleRows: docsPageRows(
				aggregated.rows,
				agentOwnedGroupIds,
				agentShown,
			),
			loaded:
				standaloneResult.type === "complete" &&
				groupsResult.type === "complete",
		}
	}, [
		standaloneProposals,
		groups,
		agentOwnedGroupIds,
		agentShown,
		standaloneResult.type,
		groupsResult.type,
	])
}
